/*
 * File: ShareStoryFlow.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 22, 2025 at 10:22 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import validator from 'validator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { toastMessage, toastSuccess } from '../../utilities/toaster';
import { renderPrimaryResource } from '../../utilities/resources';

// Services
import { submitStory, submitStoryUpdate, uploadStoryMedia } from '../../services/story';
import { recordPlatformMovement } from '../../services/action';

// Store
import { fetchTags } from '../../store/slices/storyTag/storyTag.slice';

// Hooks
import { useRoutingBlocker } from '../../hooks/useRoutingBlocker';

// Components
import { LocaleLink } from '../LocaleLink';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { ToolTip } from '../ToolTip';
import { Padding } from '../Padding';
import { BreakpointModal } from '../BreakpointModal';
import { StoryModal } from '../StoryModal';

// Step Components
import { GetStarted } from './GetStarted';
import { SelectMedium } from './SelectMedium';
import { CreateTextStory } from './CreateTextStory';
import { CreateImageStory } from './CreateImageStory';
import { CreateHealingMessage } from './CreateHealingMessage';
import { CreateHopeMessage } from './CreateHopeMessage';
import { CreateTitle } from './CreateTitle';
import { SelectTags } from './SelectTags';
import { EmailAgreements } from './EmailAgreements';
import { Resources } from './Resources';
import { EmailOptOutConfirmationModal } from '../EmailOptOutConfirmationModal';

// Constants
import { AVAILABLE_I18N_NAMESPACES, DEFAULT_COUNTRY } from '../../../constants/base.constants';
import { STORY_MEDIUM_OPTIONS } from './SelectMedium/SelectMedium';

// Styles
import * as S from './ShareStoryFlow.styles';


/**
 * Constants
 */

const STEPS = {
	ONBOARDING: 'onboarding',
	CREATE_HEALING_MESSAGE: 'create_healing_message',
	CREATE_HOPE_MESSAGE: 'create_hope_message',
	CREATE_TEXT_STORY: 'create_text_story',
	CREATE_ARTWORK_STORY: 'create_artwork_story',
	CREATE_TITLE: 'create_title',
	EMAIL_AGREEMENTS: 'email_agreements',
	RESOURCES: 'resources',
	SELECT_MEDIUM: 'select_medium',
	SELECT_TAGS: 'select_tags'
};


/**
 * Component
 */

export const ShareStoryFlow = ({
	className, context, aControl, bControl, currentStory
}) => {

	// Create reference for step components
	const getStartedStepRef = useRef();
	const selectMediumStepRef = useRef();
	const createTextStoryStepRef = useRef();
	const createImageStoryStepRef = useRef();
	const createHealingMessageStepRef = useRef();
	const createHopeMessageStepRef = useRef();
	const createTitleStepRef = useRef();
	const selectTagsStepRef = useRef();
	const emailAgreementsStepRef = useRef();
	const resourcesStepRef = useRef();

	// Create state
	const [currentStep, setCurrentStep] = useState(1);
	const [isScrolled, setIsScrolled] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
	const [isEmailOptOutConfirmationOpen, setIsEmailOptOutConfirmationOpen] = useState(false);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get resources manifest from hook
	const resourceManifest = useSelector((state) => state.resourceManifest.resources);

	// Get primary resource from manifest
	const primaryResource = useMemo(() => {
		if (resourceManifest && resourceManifest.length > 0) {
			const countryResource = resourceManifest.find((resource) => resource.reference_countries.includes(locale.countryId));
			if (countryResource) {
				return countryResource;
			}
			return resourceManifest.find((resource) => resource.reference_countries.includes(DEFAULT_COUNTRY)) || resourceManifest[0];
		}
		return null;
	}, [locale, resourceManifest]);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Get partner features
	const partnerFeatures = useMemo(() => partner?.features?.stories, [partner]);
	const partnerAgreementContent = useMemo(() => partner?.content?.pages?.share_story?.components?.agreements, [partner]);
	const breakpoints = useMemo(() => partner?.content?.pages?.share_story?.components?.breakpoints, [partner]);

	// Create breakpoints payload
	const breakpointPayload = {};
	if (breakpoints && breakpoints.length > 0) {
		breakpoints.forEach((breakpoint) => {
			breakpointPayload[breakpoint.name] = false;
		});
	}

	// Create content state
	const [storyContent, setStoryContent] = useState(null);
	const [artworkArtist, setArtworkArtist] = useState(null);
	const [artworkCaption, setArtworkCaption] = useState(null);
	const [artworkFile, setArtworkFile] = useState(null);
	const [storyTitle, setStoryTitle] = useState(null);
	const [healingMessage, setHealingMessage] = useState(null);
	const [hopeMessage, setHopeMessage] = useState(null);
	const [selectedMedium, setSelectedMedium] = useState(null);
	const [submissionEmail, setSubmissionEmail] = useState(null);
	const [termsChecked, setTermsChecked] = useState(false);
	const [socialShareChecked, setSocialShareChecked] = useState(false);
	const [noContactChecked, setNoContactChecked] = useState(false);
	const [newsletterChecked, setNewsletterChecked] = useState(false);
	const [publishMainChecked, setPublishMainChecked] = useState(partnerFeatures.default_publish_main);
	const [additionalAgreementsChecked, setAdditionalAgreementsChecked] = useState({});
	const [selectedTags, setSelectedTags] = useState([]);
	const [breakpointsOpen, setBreakpointsOpen] = useState(breakpointPayload);

	// Create steps for context
	const stepFlow = useMemo(() => (context === 'story' ? [
		STEPS.ONBOARDING,
		STEPS.SELECT_MEDIUM,
		...selectedMedium === STORY_MEDIUM_OPTIONS.TEXT ? [
			STEPS.CREATE_TEXT_STORY,
		] : [
			STEPS.CREATE_ARTWORK_STORY,
		],
		STEPS.CREATE_HEALING_MESSAGE,
		STEPS.CREATE_HOPE_MESSAGE,
		STEPS.CREATE_TITLE,
		STEPS.SELECT_TAGS,
		STEPS.EMAIL_AGREEMENTS,
		STEPS.RESOURCES
	] : [
		STEPS.SELECT_MEDIUM,
		...selectedMedium === STORY_MEDIUM_OPTIONS.TEXT ? [
			STEPS.CREATE_TEXT_STORY,
		] : [
			STEPS.CREATE_ARTWORK_STORY,
		],
		STEPS.CREATE_HEALING_MESSAGE,
		STEPS.CREATE_HOPE_MESSAGE,
		STEPS.CREATE_TITLE
	]), [context, selectedMedium]);

	// Get current story tags from hook
	const { status: tagStatus, value: storyTags } = useSelector((state) => state.storyTag);

	// Create references for components
	const isMounted = useRef(true);
	const toggleAnimation = useRef(false);
	const animationDirection = useRef(1);
	const breakpointsComplete = useRef(breakpointPayload);
	const emailOptOutSubmissionConfirmationComplete = useRef(false);

	// Use hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Set prompt to prevent navigation if story in progress
	useRoutingBlocker(t(context === 'story' ? 'pageShareStory.preventNavigationWarningStory' : 'pageShareStory.preventNavigationWarningUpdate'), currentStep > 1 && currentStep < stepFlow.length);

	// Update current step
	const moveForward = () => {
		const step = currentStep + 1;
		toggleAnimation.current = !toggleAnimation.current;
		animationDirection.current = step > currentStep ? 1 : -1;
		setCurrentStep(step);
	};
	const moveBackward = () => {
		const step = currentStep - 1;
		toggleAnimation.current = !toggleAnimation.current;
		animationDirection.current = step > currentStep ? 1 : -1;
		setCurrentStep(step);
	};

	// Handle record movement
	const recordMovement = async () => {
		try {

			// Get parameters from location
			const { pathname } = location;

			// Get step component name
			const componentName = stepFlow[currentStep - 1];

			// Record platform movement
			await recordPlatformMovement({
				path: `${pathname.replace(/\/$/, '')}/${componentName}`,
			});

		} catch (e) { }
	};

	// Handle submit story
	const handleSubmitStory = async () => {

		// Validate narrative story parameters
		if (selectedMedium === STORY_MEDIUM_OPTIONS.TEXT) {
			if (validator.isEmpty(storyContent, { ignore_whitespace: true })) {
				toastMessage(uiMode, partner, t(context === 'story' ? 'pageShareStory.missingStoryError' : 'pageShareStory.missingUpdateError'));
				return;
			}
		}

		// Validate artwork parameters
		if (selectedMedium === STORY_MEDIUM_OPTIONS.IMAGE) {
			if (!artworkFile) {
				toastMessage(uiMode, partner, t('pageShareStory.missingArtworkError'));
				return;
			}
		}

		// Validate parameters for context
		if (context === 'story') {

			// Validate general parameters
			if (!submissionEmail || !validator.isEmail(submissionEmail)) {
				toastMessage(uiMode, partner, t('pageShareStory.missingEmailError'));
				return;
			}
			if (termsChecked !== true) {
				toastMessage(uiMode, partner, t('pageShareStory.missingTermsError'));
				return;
			}

			// Validate required additional agreements
			if (partnerAgreementContent?.additional_agreements && partnerAgreementContent?.additional_agreements.length) {
				for (let i = 0; i < partnerAgreementContent.additional_agreements.length; i += 1) {

					// Get parameters
					const { name, is_required: isRequired, error } = partnerAgreementContent.additional_agreements[i];

					// Validate if required
					if (isRequired) {
						if (!additionalAgreementsChecked[name]) {
							toastMessage(uiMode, partner, error[locale.localeBaseId]);
							return;
						}
					}
				}
			}

			// Open email opt-out submission confirmation if necessary
			if (noContactChecked && !emailOptOutSubmissionConfirmationComplete.current) {
				setIsEmailOptOutConfirmationOpen(true);
				return;
			}
		}

		// Open breakpoints if necessary
		if (breakpoints && breakpoints.length > 0) {

			// Check completion for each breakpoint
			for (let i = 0; i < breakpoints.length; i += 1) {

				// Get breakpoint parameters
				const { name } = breakpoints[i];

				// Set breakpoint open if not complete
				if (!breakpointsComplete.current[name]) {
					setBreakpointsOpen({ ...breakpointsOpen, [name]: true });
					return;
				}
			}
		}

		// Update submission state
		setIsSubmitting(true);

		// Upload media if necessary
		let mediaId = null;
		if (selectedMedium === STORY_MEDIUM_OPTIONS.IMAGE) {
			try {

				// Upload story media
				const { data } = await uploadStoryMedia({ file: artworkFile });

				// Parse response
				if (data && data.media_id != null) {
					mediaId = data.media_id;
				}

			} catch (e) {

				// Update submission state
				setIsSubmitting(false);

				// Show error
				toastMessage(uiMode, partner, t(context === 'story' ? 'pageShareStory.storySubmissionError' : 'pageShareStory.updateSubmissionError'));

				// Return
				return;
			}
		}

		// Create parameters
		const params = context === 'story' ? {
			socialAllowed: socialShareChecked,
			newsletterAllowed: newsletterChecked,
			publishMainAllowed: publishMainChecked,
			emailAllowed: !noContactChecked,
			tags: (selectedTags || []).map((tag) => tag.tag_id),
		} : {
			a_control: aControl,
			b_control: bControl,
		};

		// Add text parameters
		if (selectedMedium === STORY_MEDIUM_OPTIONS.TEXT) {
			params.story = storyContent.trim();
		}

		// Add media parameters
		if (selectedMedium === STORY_MEDIUM_OPTIONS.IMAGE) {
			params.mediaId = mediaId;
			if (artworkArtist) params.artworkArtist = artworkArtist.trim();
			if (artworkCaption) params.artworkCaption = artworkCaption.trim();
		}

		// Add general parameters
		if (storyTitle) params.title = storyTitle.trim();
		if (healingMessage) params.healingMessage = healingMessage.trim();
		if (hopeMessage) params.hopeMessage = hopeMessage.trim();
		if (submissionEmail) params.email = submissionEmail.trim();

		// Get reCAPTCHA token
		if (context === 'story') {
			try { params.recaptchaToken = await executeRecaptcha('submit_story'); } catch (_) {}
		}

		// Attempt story submission
		try {

			// Submit story
			if (context === 'story') {

				// Submit story
				await submitStory(params);

				// Trigger GTM event
				try {
					window.dataLayer.push({ event: 'submit_story' });
				} catch (err) {}

				// Move to next step
				moveForward();

			} else {

				// Submit update
				await submitStoryUpdate(params);

				// Show success message
				toastSuccess(uiMode, partner, t('pageShareStory.updateSuccessMessage'));

				// Redirect back to update dashboard
				setTimeout(() => {
					navigate(`/update/${aControl}/${bControl}`);
				}, 4000);
			}
		} catch (e) {

			// Update submission state
			setIsSubmitting(false);

			// Show error
			toastMessage(uiMode, partner, t(context === 'story' ? 'pageShareStory.storySubmissionError' : 'pageShareStory.updateSubmissionError'));
		}
	};

	// Handle actions on no contact checked
	useEffect(() => {
		if (noContactChecked) {

			// Reset submission completion
			emailOptOutSubmissionConfirmationComplete.current = false;
		}
	}, [noContactChecked]);

	// Handle actions on step change
	useEffect(() => {

		// Scroll to top of component
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 300);

		// Record user movement
		recordMovement();

	}, [currentStep]);

	// Handle actions on component load
	useEffect(() => {

		// Fetch story tags if necessary
		if (tagStatus !== 'final') {

			// Fetch story tags
			dispatch(fetchTags());
		}

		// Initialize temporary state
		isMounted.current = true;

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current) {

					// Get current scroll status
					const st = document.scrollingElement.scrollTop;

					// Update scrolled status
					setIsScrolled(st >= 20);
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render primary resource
	const renderedPrimaryResource = useMemo(() => renderPrimaryResource(primaryResource, locale, t), [primaryResource, locale, t]);

	// Render component
	return (
		<S.Wrapper className={className}>

			{/* Animation Panel */}
			<S.AnimationPanel>

				{/* Sky Component */}
				<S.SkyContainer className={`step${currentStep}`}>
					<S.Sky1 $isVisible={currentStep === 1} />
					<S.Sky2 $isVisible={currentStep === 2} />
					<S.Sky3 $isVisible={currentStep === 3} />
					<S.Sky5 $isVisible={currentStep === 4} />
					<S.Sky6 $isVisible={currentStep === 5} />
					<S.Sky7 $isVisible={currentStep === 6} />
					<S.Sky8 $isVisible={currentStep === 7} />
					<S.Sky9 $isVisible={currentStep === 8} />
					<S.Sky10 $isVisible={currentStep === 9} />
				</S.SkyContainer>

				{/* Sun Component */}
				<S.SunContainer className={`step${currentStep}`}>
					<S.SunInnerContainer className={`step${currentStep}`}>
						<S.Sun1 $isVisible={currentStep === 1} />
						<S.Sun2 $isVisible={currentStep === 2} />
						<S.Sun3 $isVisible={currentStep === 3} />
						<S.Sun5 $isVisible={currentStep === 4} />
						<S.Sun6 $isVisible={currentStep === 5} />
						<S.Sun7 $isVisible={currentStep === 6} />
						<S.Sun8 $isVisible={currentStep === 7} />
						<S.Sun9 $isVisible={currentStep === 8} />
						<S.Sun10 $isVisible={currentStep === 9} />
					</S.SunInnerContainer>
				</S.SunContainer>

				{/* Sun Reflection Component */}
				<S.SunReflectionContainer className={`step${currentStep}`}>
					<S.SunReflectionInnerContainer className={`step${currentStep}`}>
						<S.Sun1 $isVisible={currentStep === 1} />
						<S.Sun2 $isVisible={currentStep === 2} />
						<S.Sun3 $isVisible={currentStep === 3} />
						<S.Sun5 $isVisible={currentStep === 4} />
						<S.Sun6 $isVisible={currentStep === 5} />
						<S.Sun7 $isVisible={currentStep === 6} />
						<S.Sun8 $isVisible={currentStep === 7} />
						<S.Sun9 $isVisible={currentStep === 8} />
						<S.Sun10 $isVisible={currentStep === 9} />
					</S.SunReflectionInnerContainer>
				</S.SunReflectionContainer>

				{/* Ocean Reflection */}
				<S.OceanContainer className={`step${currentStep}`}>
					<S.Ocean1 $isVisible={currentStep === 1} />
					<S.Ocean2 $isVisible={currentStep === 2} />
					<S.Ocean3 $isVisible={currentStep === 3} />
					<S.Ocean5 $isVisible={currentStep === 4} />
					<S.Ocean6 $isVisible={currentStep === 5} />
					<S.Ocean7 $isVisible={currentStep === 6} />
					<S.Ocean8 $isVisible={currentStep === 7} />
					<S.Ocean9 $isVisible={currentStep === 8} />
					<S.Ocean10 $isVisible={currentStep === 9} />
				</S.OceanContainer>

			</S.AnimationPanel>

			{/* Sharing Container */}
			<S.ContainerPanel>

				{/* Share Header */}
				<S.Header $isScrolled={isScrolled} className="animate">

					{/* Back to Community */}
					<S.HeaderModule>
						{context === 'story' ? (
							<LocaleLink to="/">
								<Button variant="text">
									<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
									<Typography>
										<Trans t={t} i18nKey="actions.community">
											Community
										</Trans>
									</Typography>
								</Button>
							</LocaleLink>
						) : (
							<LocaleLink to={`/update/${aControl}/${bControl}`}>
								<Button variant="text">
									<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
									<Typography>
										<Trans t={t} i18nKey="pageShareStory.backToStory">
											Back to story
										</Trans>
									</Typography>
								</Button>
							</LocaleLink>
						)}
					</S.HeaderModule>

					{/* Security Indicator */}
					<S.HeaderModule $fullWidth>
						<ToolTip />
						<S.LockButton icon={['fas', 'lock']} size={1.1} data-tip={t('pageShareStory.securityTooltip')} />
					</S.HeaderModule>

					{/* Resources */}
					<S.HeaderModule>
						<LocaleLink to="/resources">
							<Button variant="text">
								<Trans t={t} i18nKey="actions.resources">
									Resources
								</Trans>
							</Button>
						</LocaleLink>
					</S.HeaderModule>
				</S.Header>

				{/* Header Card */}
				{context === 'update' && (
					<S.HeaderCardPadding left right>
						<S.HeaderCard onClick={() => { setIsStoryModalOpen(true); }}>
							<Typography
								center
								tag="p"
								variation="3"
								weight="medium"
							>
								<Trans t={t} i18nKey="pageShareStory.storyPopoutAction">
									You are adding an update to your story. Click to view your original story in a pop-out.
								</Trans>
							</Typography>
						</S.HeaderCard>
					</S.HeaderCardPadding>
				)}

				{/* Step Container */}
				<S.StepContainer className="animate">
					<S.Step className={stepFlow[currentStep - 1] === STEPS.ONBOARDING ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<GetStarted
								ref={getStartedStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.ONBOARDING}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.SELECT_MEDIUM ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<SelectMedium
								ref={selectMediumStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.SELECT_MEDIUM}
								selectedMedium={selectedMedium}
								setSelectedMedium={setSelectedMedium}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.CREATE_TEXT_STORY ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<CreateTextStory
								ref={createTextStoryStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.CREATE_TEXT_STORY}
								storyContent={storyContent}
								setStoryContent={setStoryContent}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.CREATE_ARTWORK_STORY ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<CreateImageStory
								ref={createImageStoryStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.CREATE_ARTWORK_STORY}
								artworkFile={artworkFile}
								setArtworkFile={setArtworkFile}
								artworkArtist={artworkArtist}
								setArtworkArtist={setArtworkArtist}
								artworkCaption={artworkCaption}
								setArtworkCaption={setArtworkCaption}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.CREATE_HEALING_MESSAGE ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<CreateHealingMessage
								ref={createHealingMessageStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.CREATE_HEALING_MESSAGE}
								healingMessage={healingMessage}
								setHealingMessage={setHealingMessage}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.CREATE_HOPE_MESSAGE ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<CreateHopeMessage
								ref={createHopeMessageStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.CREATE_HOPE_MESSAGE}
								hopeMessage={hopeMessage}
								setHopeMessage={setHopeMessage}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.CREATE_TITLE ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<CreateTitle
								ref={createTitleStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.CREATE_TITLE}
								storyTitle={storyTitle}
								setStoryTitle={setStoryTitle}
								context={context}
								handleSubmitStory={handleSubmitStory}
								isSubmitting={isSubmitting}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.SELECT_TAGS ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<SelectTags
								ref={selectTagsStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.SELECT_TAGS}
								storyTags={storyTags}
								selectedTags={selectedTags}
								setSelectedTags={setSelectedTags}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.EMAIL_AGREEMENTS ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<EmailAgreements
								ref={emailAgreementsStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								handleSubmitStory={handleSubmitStory}
								isVisible={stepFlow[currentStep - 1] === STEPS.EMAIL_AGREEMENTS}
								submissionEmail={submissionEmail}
								setSubmissionEmail={setSubmissionEmail}
								termsChecked={termsChecked}
								setTermsChecked={setTermsChecked}
								socialShareChecked={socialShareChecked}
								setSocialShareChecked={setSocialShareChecked}
								noContactChecked={noContactChecked}
								setNoContactChecked={setNoContactChecked}
								newsletterChecked={newsletterChecked}
								setNewsletterChecked={setNewsletterChecked}
								publishMainChecked={publishMainChecked}
								setPublishMainChecked={setPublishMainChecked}
								additionalAgreementsChecked={additionalAgreementsChecked}
								setAdditionalAgreementsChecked={setAdditionalAgreementsChecked}
								isSubmitting={isSubmitting}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
					<S.Step className={stepFlow[currentStep - 1] === STEPS.RESOURCES ? 'animate show' : 'animate'}>

						{/* Padding */}
						<Padding left right>
							<Resources
								ref={resourcesStepRef}
								moveForward={moveForward}
								moveBackward={moveBackward}
								isVisible={stepFlow[currentStep - 1] === STEPS.RESOURCES}
								context={context}
							/>
						</Padding>

						{/* Share Footer */}
						<S.ShareFooter left right>
							{renderedPrimaryResource}
						</S.ShareFooter>

					</S.Step>
				</S.StepContainer>
			</S.ContainerPanel>

			{/* Breakpoint Modals */}
			{breakpoints && breakpoints.length > 0 && breakpoints.map((breakpoint) => (
				<BreakpointModal
					key={breakpoint.name}
					isOpen={breakpointsOpen[breakpoint.name]}
					content={breakpoint.content}
					buttons={breakpoint.buttons}
					handleComplete={() => {

						// Update breakpoint visibility
						setBreakpointsOpen({ ...breakpointsOpen, [breakpoint.name]: false });

						// Update breakpoint completion
						breakpointsComplete.current[breakpoint.name] = true;

						// Continue submission flow
						handleSubmitStory();
					}}
				/>
			))}

			{/* Email Opt-Out Confirmation Modal */}
			<EmailOptOutConfirmationModal
				isOpen={isEmailOptOutConfirmationOpen}
				handleCancel={() => {

					// Update modal visibility
					setIsEmailOptOutConfirmationOpen(false);

					// Update option
					setNoContactChecked(false);

				}}
				handleConfirm={() => {

					// Update modal visibility
					setIsEmailOptOutConfirmationOpen(false);

					// Update submission completion
					emailOptOutSubmissionConfirmationComplete.current = true;

					// Continue submission flow
					handleSubmitStory();
				}}
			/>

			{/* Story Modal */ }
			<StoryModal
				isOpen={isStoryModalOpen}
				handleClose={() => { setIsStoryModalOpen(false); }}
				story={currentStory}
			/>

		</S.Wrapper>
	);
};


/**
 * Configuration
 */

ShareStoryFlow.displayName = 'ShareStoryFlow';
ShareStoryFlow.propTypes = {
	className: PropTypes.string,
	context: PropTypes.oneOf(['story', 'update']),
	aControl: PropTypes.string,
	bControl: PropTypes.string,
	currentStory: PropTypes.shape()
};
ShareStoryFlow.defaultProps = {
	className: null,
	context: 'story',
	aControl: null,
	bControl: null,
	currentStory: null
};
